import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type DiscoveryProceedPauseApi = {
    decisionPending: boolean;
    agentProcessing: boolean;
    /** Deep guided path (Git + discovery + build contract) — formerly “Proceed”. */
    onGuidedDiscovery: () => void | Promise<void>;
    /** Generate user flow via `gen_userflow` — formerly “Build with agents” on the form. */
    onInstantDraft: () => void | Promise<void>;
    /** Only this chat row may render the two actions; duplicates in history stay hidden. */
    discoveryActionsMessageId?: string | null;
    /** False when repo, branch, and GitHub token are not all set (project Git settings or global fallback). */
    githubSettingsReady?: boolean;
    /** After first readiness check; avoids showing the Git hint before the initial load. */
    githubSettingsGateResolved?: boolean;
    /** When false, Guided discovery is disabled (e.g. gate still loading). */
    githubProceedAllowed?: boolean;
    /** Shown near Guided discovery when Git is incomplete after the gate resolves. */
    githubSettingsHint?: string;
};

type DiscoveryProceedPauseContextValue = {
    api: DiscoveryProceedPauseApi | null;
    register: (next: DiscoveryProceedPauseApi | null) => void;
};

const DiscoveryProceedPauseContext = createContext<DiscoveryProceedPauseContextValue | null>(null);

export function DiscoveryProceedPauseProvider({ children }: { children: React.ReactNode }) {
    const [api, setApi] = useState<DiscoveryProceedPauseApi | null>(null);
    const register = useCallback((next: DiscoveryProceedPauseApi | null) => {
        setApi(next);
    }, []);
    const value = useMemo(() => ({ api, register }), [api, register]);
    return (
        <DiscoveryProceedPauseContext.Provider value={value}>{children}</DiscoveryProceedPauseContext.Provider>
    );
}

/** Used by ProductDiscoveryChat to expose Guided discovery / Instant draft handlers to VishCoderChatPanel. */
export function useDiscoveryProceedPauseRegister(): DiscoveryProceedPauseContextValue {
    const ctx = useContext(DiscoveryProceedPauseContext);
    if (!ctx) {
        return {
            api: null,
            register: () => {},
        };
    }
    return ctx;
}

/** Used by VishCoderChatPanel to read inline discovery actions (optional; null when not on create-project flow). */
export function useDiscoveryProceedPauseApi(): DiscoveryProceedPauseApi | null {
    const ctx = useContext(DiscoveryProceedPauseContext);
    return ctx?.api ?? null;
}
